import { useState, useEffect, useRef } from 'react';

import { getVendorCount } from '../adapters/saddlebag';
import { COMPONENT_NAME } from '../constants';

import type { Logger } from '@web-platform/global-components-types/utils';

/**
 * Hook for fetching vendor count asynchronously.
 *
 * getVendorCount() returns a Promise, so this hook handles the async loading
 * and provides the count once resolved.
 *
 * @param logger - Logger instance for error reporting
 * @returns The vendor count (null while loading, number once resolved)
 */
export function useVendorCount(logger: Logger): number | null {
  const [vendorCount, setVendorCount] = useState<number | null>(null);
  const loggerRef = useRef(logger);

  useEffect(() => {
    loggerRef.current = logger;
  }, [logger]);

  useEffect(() => {
    let mounted = true;

    getVendorCount()
      .then((count) => {
        if (mounted) {
          setVendorCount(count);
        }
      })
      .catch((err) => {
        loggerRef.current.logWarn({
          message: `Failed to fetch vendor count: ${err instanceof Error ? err.message : String(err)}`,
          component: COMPONENT_NAME,
        });
      });

    return () => {
      mounted = false;
    };
  }, []);

  return vendorCount;
}
